import type {
  TagDetails,
  ManyRollupsWithTags,
  RollupWithTagsAndCounts,
  ManyRollupsWithTagsAndRange,
  ProductionSegmentType,
} from "../../../types/types";
import { PnlCategoriesEnum } from "../../../types/enums";
import { useSelector } from "store/useSelector";
import { uniqBy, flatMap } from "lodash";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useShouldFetchFullRangeDataDump } from "components/DataGetter/DataGetter";
import { useMapDataDumpToRollup } from "components/Dashboard/TrendChart/TrendChartContent/trendChartContentUtils";
import {
  useFullRangeDataDump,
  useGetPageFromPath,
} from "utils/customHooks/genericHooks";
import { useMemo } from "react";
import { isEmptyObject } from "components/Dashboard/TrendChart/trendChartUtils";

/** get the list of tags from state.legacy.rollupsMetric,
 * @param shouldIncludePnlCategories create additional tags to represent the pnl categories
 * @param timelineOnly only include tags which are on the timeline
 */
export function useTagList({
  shouldIncludePnlCategories = true,
  singleTimelineOnly = false,
} = {}): TagDetails[] {
  const { isProcessTilesPage } = useGetPageFromPath();
  const shouldFetchFullRangeDataDump = useShouldFetchFullRangeDataDump();
  const fullRangeDataDump = useFullRangeDataDump();
  const allProcessesRollupMetric = useSelector(
    state => state.legacy.allProcessesRollupMetric
  );
  const segments = useSelector(state => state.legacy.segments);
  const singleTimelineMachemetryId = useProcessIdOnSingleProcessPage();
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  return useMemo(() => {
    if (singleTimelineOnly) {
      // only include tags which are on the timeline
      const singleTimelineSegments =
        singleTimelineMachemetryId && segments[singleTimelineMachemetryId];
      return !singleTimelineSegments
        ? []
        : getTagsFromTimelineSegments(
            singleTimelineSegments,
            shouldIncludePnlCategories
          );
    } else {
      let tagList: TagDetails[] = [];

      if (
        shouldFetchFullRangeDataDump &&
        fullRangeDataDump &&
        !isProcessTilesPage
      ) {
        tagList = getTagsAndPnlCategoriesFromRollupMetric(
          {
            all: mapDataDumpToRollup(fullRangeDataDump),
          },
          shouldIncludePnlCategories
        );
      } else if (isEmptyObject(segments)) {
        tagList = getTagsAndPnlCategoriesFromRollupMetric(
          {
            all: allProcessesRollupMetric,
          },
          shouldIncludePnlCategories
        );
      } else {
        tagList = getTagsFromTimelineSegments(
          flatMap(Object.values(segments), x => x),
          shouldIncludePnlCategories
        );
      }

      // append any tags from timeline segments as well (e.g. if outside of focus range, these are not included in fullRangeDataDump) using getTagsFromTimelineSegments
      if (!isEmptyObject(segments)) {
        tagList = uniqBy(
          [
            ...tagList,
            ...getTagsFromTimelineSegments(
              flatMap(Object.values(segments), x => x),
              shouldIncludePnlCategories
            ),
          ],
          value => value.name + value.pnlCategory
        );
      }

      return tagList;
    }
  }, [
    singleTimelineOnly,
    singleTimelineMachemetryId,
    segments,
    shouldFetchFullRangeDataDump,
    fullRangeDataDump,
    mapDataDumpToRollup,
    shouldIncludePnlCategories,
    allProcessesRollupMetric,
    isProcessTilesPage,
  ]);
}

function getTagsAndPnlCategoriesFromRollupMetric(
  rollupsMetricByProcess: ManyRollupsWithTagsAndRange,
  shouldIncludePnlCategories: boolean
): TagDetails[] {
  return Object.entries(rollupsMetricByProcess as ManyRollupsWithTags).reduce(
    (
      accumulator: TagDetails[],
      [procId, procRollup]: [string, RollupWithTagsAndCounts]
    ) => {
      const miniTagList: TagDetails[] = Object.entries(procRollup.tags).reduce(
        (acc: TagDetails[], [pnlCategory, tagsObj]) => {
          const tags =
            tagsObj && pnlCategory != "uncollected"
              ? Object.keys(tagsObj).map(name => ({ name, pnlCategory }))
              : [];
          return [...acc, ...(tags as TagDetails[])];
        },
        [] as TagDetails[]
      );

      // pnl categories [producing, unknown, uncollected] -> need to add the category name here, because these categories don't have tags and so are summed up into one tag
      if (shouldIncludePnlCategories) {
        if (procRollup.producing) {
          miniTagList.push({
            name: "Producing",
            pnlCategory: PnlCategoriesEnum.producing,
          });
        }
        if (procRollup.unknown) {
          miniTagList.push({
            name: "Unknown",
            pnlCategory: PnlCategoriesEnum.unknown,
          });
        }
        if (procRollup.uncollected) {
          miniTagList.push({
            name: "Uncollected",
            pnlCategory: PnlCategoriesEnum.uncollected,
          });
        }
      }

      return uniqBy(
        [...accumulator, ...miniTagList],
        value => value.name + value.pnlCategory
      );
    },
    []
  );
}

function getTagsFromTimelineSegments(
  singleTimelineSegments: ProductionSegmentType[],
  shouldIncludePnlCategories: boolean
): TagDetails[] {
  let tagsList = singleTimelineSegments.reduce(
    (accumulator: TagDetails[], segment) => {
      if (
        segment.tag?.name &&
        // if we haven't already added this tag
        !accumulator.find(
          tag =>
            tag.name === segment.tag?.name ||
            segment.pnl?.category == PnlCategoriesEnum.uncollected
        )
      ) {
        // add it to the accumulator
        const tagObj: TagDetails = {
          name: segment.tag.name,
          pnlCategory: PnlCategoriesEnum[segment.pnl.category],
        };
        return [...accumulator, tagObj];
      }
      return accumulator;
    },
    [] as TagDetails[]
  );

  // pnl categories [producing, unknown, uncollected] -> need to add the category name here, because these categories don't have tags and so are summed up into one tag
  if (shouldIncludePnlCategories) {
    const hasProducingSegment = singleTimelineSegments.some(
      segment => segment.pnl.category === PnlCategoriesEnum.producing
    );
    if (hasProducingSegment) {
      tagsList = [
        {
          name: "Producing",
          pnlCategory: PnlCategoriesEnum.producing,
        },
        ...tagsList,
      ];
    }
    const hasUnknownSegment = singleTimelineSegments.some(
      segment => segment.pnl.category === PnlCategoriesEnum.unknown
    );
    if (hasUnknownSegment) {
      tagsList = [
        {
          name: "Unknown",
          pnlCategory: PnlCategoriesEnum.unknown,
        },
        ...tagsList,
      ];
    }
    const hasUncollectedSegment = singleTimelineSegments.some(
      segment => segment.pnl.category === PnlCategoriesEnum.uncollected
    );
    if (hasUncollectedSegment) {
      tagsList = [
        {
          name: "Uncollected",
          pnlCategory: PnlCategoriesEnum.uncollected,
        },
        ...tagsList,
      ];
    }
  }

  return uniqBy(tagsList, value => value.name + value.pnlCategory);
}
